import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'

import {
  Box,
  ButtonGroup,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import Button from '@mui/material/Button';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Questionnaires widget settings. Object under one value key
// (`questionnaires_small`):
//   { cta: boolean, mode: 'stats' | 'list' }
// - `cta`  : on/off for the top-right "goto questionnaires" button
//            (premium-locked when the questionnaire module is off). On by
//            default.
// - `mode` : which body the widget renders — 'stats' (x invited / y
//            completed counts, the default) or 'list' (the client's last 5
//            questionnaire results by updated_at, newest first). Rendered
//            as a 2-button ButtonGroup.
// No per-row visibility settings — the body is data, not config — and no
// order setting.

export type QuestionnairesSmallSettingsValue = {
  cta?: boolean;
  mode?: 'stats' | 'list';
};

type Props = {
  value: QuestionnairesSmallSettingsValue;
  onChange: (v: QuestionnairesSmallSettingsValue) => void;
  // Unused for this widget (no order setting). Kept in the shared
  // SettingsProps shape so the dialog renders it uniformly.
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.questionnaires_small';

export const CoachSettingsWidgetQuestionnairesSmall: FunctionComponent<Props> = ({
  value,
  onChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Defensive defaults so the dialog works before the first save.
  const cta = HelpersClientWidgets.getQuestionnairesSmallCtaEnabled(value);
  const mode = HelpersClientWidgets.getQuestionnairesSmallMode(value);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Widget-level settings. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.options.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.questionnairesSmallCtaIcon
            sx={{fontSize: '1.2rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.cta`)}
            value={cta}
            greying
            onChange={(c) => onChange({ ...value, cta: c })}
          />
        </Box>

        <Divider sx={{marginTop: 1}} />

        {/* Mode picker: a 2-button ButtonGroup (selected = contained, the
            CourseEdit pattern). Persisted as the `mode` key; unknown values
            fall back to 'stats' via the helper getter. Label + group on
            separate lines (flex-wrap with a full-width label). */}
        <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <Typography variant="body2" sx={{color: 'text.secondary', width: '100%'}}>
            {t(`${LABEL_BASE}.options.mode`)}
          </Typography>
          <ButtonGroup>
            {HelpersClientWidgets.questionnairesSmallModes.map((m) => {
              const selected = mode === m.key;
              const { Icon } = m;
              return <Button
                key={m.key}
                variant={selected ? "contained" : "outlined"}
                onClick={() => onChange({ ...value, mode: (m.key as 'stats' | 'list') })}
                startIcon={<Icon />}
              >
                {t(`${LABEL_BASE}.options.mode.${m.key}`)}
              </Button>;
            })}
          </ButtonGroup>
        </Box>
      </Paper>
    </Grid>
  </Grid>;
}

// Read-only status: the active mode icon (stats or list) + the CTA toggle
// icon coloured by on/off so the placed box summarises the config at a glance.
type StatusProps = {
  value: QuestionnairesSmallSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetQuestionnairesSmallStatus: FunctionComponent<StatusProps> = ({
  value
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  const cta = HelpersClientWidgets.getQuestionnairesSmallCtaEnabled(value);
  const mode = HelpersClientWidgets.getQuestionnairesSmallMode(value);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      <Box title={t(`${LABEL_BASE}.options.mode.${mode}`)}>
        {(() => {
          const modeItem = HelpersClientWidgets.questionnairesSmallModes.find((m) => m.key === mode) || HelpersClientWidgets.questionnairesSmallModes[0];
          const { Icon } = modeItem;
          return <Icon sx={{ fontSize: '1.1rem', color: theme.palette.primary.main }} />;
        })()}
      </Box>
      <Box title={t(`${LABEL_BASE}.options.cta`)}>
        <HelpersClientWidgets.questionnairesSmallCtaIcon
          sx={{ fontSize: '1.1rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
    </Box>
  </Box>;
}
